import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, ErrorBanner, Spinner } from '@/components/ui';
import { portfolioApi } from '@/services/portfolio-api';
import type { IEpicBreakdownResponse, IEpicBreakdownRow } from '@skystone/shared';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { useCardFetch } from '@/hooks/use-card-fetch';
import { useCardWidth } from '@/hooks/use-card-width';
import { CARD_WIDTH_THRESHOLDS } from '@/lib/metric-view/responsive-thresholds';
import { cn } from '@/lib/utils';
import { EpicBreakdownStacked } from './EpicBreakdownStacked';

interface IEpicBreakdownCardProps {
  filters: IMetricViewFilters;
}

type ColumnId = 'epic' | 'completion' | 'cycle' | 'throughput' | 'wip' | 'trend';
type Direction = 'asc' | 'desc';

interface ISortState {
  columnId: ColumnId | null;
  direction: Direction;
}

const COLUMN_LABELS: Readonly<Record<ColumnId, string>> = {
  epic: 'Epic',
  completion: 'Completion',
  cycle: 'Cycle (median)',
  throughput: 'Throughput',
  wip: 'WIP',
  trend: 'Trend',
};

const TREND_ORDER: Readonly<Record<string, number>> = {
  up: 3,
  flat: 2,
  down: 1,
};

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b);
}

function compareWithNullsLast(a: number | null, b: number | null, direction: Direction): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return direction === 'asc' ? a - b : b - a;
}

function sortValue(row: IEpicBreakdownRow, columnId: ColumnId): number | null | string {
  if (columnId === 'epic') return `${row.epicName}|${row.epicKey}`;
  if (columnId === 'completion') return row.total === 0 ? null : row.completionRate;
  if (columnId === 'cycle') return row.cycleMedianDays;
  if (columnId === 'throughput') return row.throughputPerWeek;
  if (columnId === 'wip') return row.wipCount;
  // trend
  if (row.trendDirection === null) return null;
  return TREND_ORDER[row.trendDirection] ?? 0;
}

function sortRows(rows: readonly IEpicBreakdownRow[], sort: ISortState): IEpicBreakdownRow[] {
  if (sort.columnId === null) return [...rows];
  const id = sort.columnId;
  const dir = sort.direction;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, id);
    const bv = sortValue(b, id);
    let cmp: number;
    if (typeof av === 'string' || typeof bv === 'string') {
      const aStr = typeof av === 'string' ? av : '';
      const bStr = typeof bv === 'string' ? bv : '';
      cmp = dir === 'asc' ? compareStrings(aStr, bStr) : compareStrings(bStr, aStr);
    } else {
      cmp = compareWithNullsLast(av, bv, dir);
    }
    if (cmp !== 0) return cmp;
    return a.epicKey.localeCompare(b.epicKey);
  });
}

function formatNumber(value: number | null, digits = 1): string {
  if (value === null) return '—';
  const factor = Math.pow(10, digits);
  return (Math.round(value * factor) / factor).toString();
}

function TrendCell({ direction }: Readonly<{ direction: IEpicBreakdownRow['trendDirection'] }>) {
  if (direction === 'up') return <span className="text-error">↑ worsening</span>;
  if (direction === 'down') return <span className="text-success">↓ improving</span>;
  if (direction === 'flat') return <span className="text-muted-foreground">→ flat</span>;
  return <span className="text-muted-foreground">—</span>;
}

interface IHeaderCellProps {
  columnId: ColumnId;
  sort: ISortState;
  onSort: (id: ColumnId) => void;
  align?: 'left' | 'right';
  className?: string;
}

function HeaderCell({ columnId, sort, onSort, align = 'right', className }: Readonly<IHeaderCellProps>) {
  const isActive = sort.columnId === columnId;
  const ascArrow = sort.direction === 'asc' ? '↑' : '↓';
  const indicator = isActive ? ascArrow : '↕';
  return (
    <th
      scope="col"
      className={cn(
        'px-2 py-2 text-xs font-medium uppercase text-muted-foreground',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(columnId)}
        className={cn(
          'inline-flex items-center gap-1 rounded hover:text-foreground focus:text-foreground focus:outline-none',
          align === 'right' ? 'justify-end' : 'justify-start',
          isActive && 'text-foreground',
        )}
      >
        <span>{COLUMN_LABELS[columnId]}</span>
        <span aria-hidden="true" className={cn('text-[10px]', !isActive && 'text-muted-foreground/50')}>
          {indicator}
        </span>
      </button>
    </th>
  );
}

export function EpicBreakdownCard({ filters }: Readonly<IEpicBreakdownCardProps>) {
  const { ref, width } = useCardWidth<HTMLDivElement>();
  const useStacked = width > 0 && width < CARD_WIDTH_THRESHOLDS.epicBreakdown.stacked;

  const { data, loading, error } = useCardFetch<IEpicBreakdownResponse>(
    () => portfolioApi.getEpicBreakdown({
      from: filters.from,
      to: filters.to,
      productId: filters.productId || undefined,
      teamId: filters.teamId || undefined,
      includeArchived: filters.includeArchived || undefined,
    }),
    [filters.from, filters.to, filters.productId, filters.teamId, filters.includeArchived],
    'Failed to load per-epic breakdown.',
  );

  // Reset sort when the data changes (new fetch = new default order).
  const [sort, setSort] = useState<ISortState>({ columnId: null, direction: 'asc' });
  useEffect(() => { setSort({ columnId: null, direction: 'asc' }); }, [data]);

  const onSort = useCallback((id: ColumnId) => {
    setSort((prev) => {
      if (prev.columnId === id) {
        return { columnId: id, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { columnId: id, direction: 'asc' };
    });
  }, []);

  const sortedRows = useMemo(
    () => sortRows(data?.epics ?? [], sort),
    [data, sort],
  );

  const rowCount = data?.epics.length ?? 0;

  return (
    <Card ref={ref}>
      <CardHeader>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Per-Epic Breakdown — All Product Flow Metrics
        </h3>
        <p className="text-[11px] text-muted-foreground">
          Top active epics across all products and teams.
        </p>
      </CardHeader>
      <CardContent>
        {loading && <div className="flex h-40 items-center justify-center"><Spinner /></div>}
        {!loading && error && <ErrorBanner message={error} />}
        {!loading && !error && rowCount === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No active epics match the current filters.
          </p>
        )}
        {!loading && !error && rowCount > 0 && useStacked && (
          <EpicBreakdownStacked rows={sortedRows} />
        )}
        {!loading && !error && rowCount > 0 && !useStacked && (
          <div className="max-h-[520px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border">
                  <HeaderCell columnId="epic" sort={sort} onSort={onSort} align="left" className="min-w-[260px]" />
                  <HeaderCell columnId="completion" sort={sort} onSort={onSort} className="min-w-[100px]" />
                  <HeaderCell columnId="cycle" sort={sort} onSort={onSort} className="min-w-[120px]" />
                  <HeaderCell columnId="throughput" sort={sort} onSort={onSort} className="min-w-[110px]" />
                  <HeaderCell columnId="wip" sort={sort} onSort={onSort} className="min-w-[70px]" />
                  <HeaderCell columnId="trend" sort={sort} onSort={onSort} align="left" className="min-w-[120px]" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={r.epicKey} className="border-t border-border hover:bg-muted/30">
                    <td className="px-2 py-2">
                      <div className="font-medium text-foreground">{r.epicName}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.productName} · {r.epicKey}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {r.total === 0 ? (
                        <span className="text-xs text-muted-foreground">— no issues —</span>
                      ) : (
                        <span>
                          <span className="text-muted-foreground">{r.done}/{r.total} · </span>
                          <span className="font-medium text-foreground">{Math.round(r.completionRate)}%</span>
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {r.cycleMedianDays === null ? '—' : `${formatNumber(r.cycleMedianDays)}d`}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {r.throughputPerWeek === null ? '—' : `${formatNumber(r.throughputPerWeek)} / wk`}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.wipCount}</td>
                    <td className="px-2 py-2 text-left">
                      <TrendCell direction={r.trendDirection} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
